import { useEffect, useState } from 'react'

// Distance (px) between the window's bottom edge and the visual viewport's
// bottom edge (`visualViewport.offsetTop + visualViewport.height`) — the
// region currently covered by the on-screen keyboard (and, where present,
// Chrome's autofill strip), read as a single absolute measurement.
//
// This is deliberately NOT applied as a compensating delta on a
// `position: sticky` element the way `useKeyboardInset` is (see that file —
// still used by DeckCreateModal, not this one). It exists to be consumed as
// the `bottom` offset of a `position: fixed` element instead: fixed elements
// resolve against the layout viewport unconditionally, so there is no
// containing-block / scroll-container baseline to get wrong, and nothing to
// double-count. See
// docs/diagnostics/composer-keyboard-positioning-measurement-2026-07-26.md
// §2.3 for why this is the mobile deck-feed composer's fix and the Learn
// answer bar (a different mechanism entirely) is not touched.
//
// Subscribed to both `resize` and `scroll`: Chrome pans the visual viewport
// to reveal a focused input *after* the keyboard resize fires, and that pan
// is a `scroll` event, not `resize`. `useKeyboardInset` deliberately omits
// `scroll` (see its header) because it was compounding an already-wrong
// baseline; that concern does not apply here since this hook's output is
// the only measurement the fixed element uses.
export function useViewportBottomGap(): number {
  const [gap, setGap] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const update = () => {
      const g = window.innerHeight - (vv.offsetTop + vv.height)
      setGap(Math.max(0, Math.round(g)))
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  return gap
}
